import type { Landmark } from '../../../data/types';
import type { Ring } from '../../../geo/types';
import { vesselOutline, vesselButtressRings } from './churchVessel';
import { conventEnclosureRings } from './conventEnclosure';
import { conventRangeRect } from './conventRange';
import { corners } from './genericBlock';
import reading from './sintGeertruidPlan.json';
import { toLayout, type PlanFrame } from './planFrame';

export function sintGeertruidReading(landmark: Landmark) {
  if (landmark.id !== 'landmark-sint-geertruid')
    throw new Error(`No Geertruid plan for ${landmark.id}`);
  return reading;
}

export function sintGeertruidFrame(landmark: Landmark): PlanFrame {
  const x = landmark.params['anchorX'],
    y = landmark.params['anchorY'];
  if (typeof x !== 'number' || typeof y !== 'number')
    throw new Error(`${landmark.id} lacks its component anchor`);
  return { origin: [x, y], heading: landmark.heading, ring: [] };
}

export const sintGeertruidChapel = (landmark: Landmark) => ({
  ...sintGeertruidReading(landmark).chapel,
  apse: 'polygonal' as const,
});

export function sintGeertruidChapelFrame(landmark: Landmark): PlanFrame {
  return sintGeertruidRangeFrame(
    sintGeertruidFrame(landmark),
    sintGeertruidReading(landmark).chapel
  );
}

export function sintGeertruidRangeFrame(
  plan: PlanFrame,
  part: { readonly u: number; readonly v: number; readonly angleDeg: number }
): PlanFrame {
  return {
    origin: toLayout(plan, [part.u, part.v]),
    heading: plan.heading + (part.angleDeg * Math.PI) / 180,
    ring: [],
  };
}

/** Grounded bodies only: neither court nor canal entry becomes a precinct slab. */
export function sintGeertruidFoundations(landmark: Landmark): Ring[] {
  const plan = sintGeertruidFrame(landmark);
  return [
    ...[
      vesselOutline(sintGeertruidChapel(landmark)),
      ...vesselButtressRings(sintGeertruidChapel(landmark)),
    ].map((r) => r.map((p) => toLayout(sintGeertruidChapelFrame(landmark), p))),
    ...sintGeertruidReading(landmark).ranges.flatMap((part) => {
      const rect = conventRangeRect(part),
        frame = sintGeertruidRangeFrame(plan, part),
        centre = 'passageU' in part ? part.passageU : 0;
      return (
        part.passage
          ? [
              { ...rect, u1: centre - part.passage / 2 },
              { ...rect, u0: centre + part.passage / 2 },
            ]
          : [rect]
      ).map((r) => corners(r).map((p) => toLayout(frame, p)));
    }),
  ];
}

export function sintGeertruidObstacles(landmark: Landmark): Ring[] {
  const plan = sintGeertruidFrame(landmark);
  return [
    ...sintGeertruidFoundations(landmark),
    ...conventEnclosureRings(sintGeertruidReading(landmark).walls).map((r) =>
      r.map((p) => toLayout(plan, p))
    ),
  ];
}

export function sintGeertruidFloors(landmark: Landmark) {
  const plan = sintGeertruidFrame(landmark),
    reading = sintGeertruidReading(landmark);
  return [
    ...reading.floors.map((f) => ({ ...f, ring: f.ring.map((p) => [p[0]!, p[1]!] as const) })),
    ...reading.ranges
      .filter((p) => p.passage)
      .map((p) => {
        const centre = 'passageU' in p ? p.passageU : 0;
        return {
          ring: corners({
            u0: centre - p.passage / 2,
            u1: centre + p.passage / 2,
            v0: -p.width / 2,
            v1: p.width / 2,
          }).map((q) =>
            toLayout({ origin: [p.u, p.v], heading: (p.angleDeg * Math.PI) / 180, ring: [] }, q)
          ),
          height: 0.025,
          garden: false,
        };
      }),
  ].map((f) => ({ ...f, ring: f.ring.map((p) => toLayout(plan, p)) }));
}
